import React, { useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { X, Check } from 'lucide-react';
import './NewsletterPopup.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const KEY = 'cl_newsletter_v1';

const NewsletterPopup = () => {
  const { lang } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const path = window.location.pathname;
    if (path.startsWith('/admin') || path.startsWith('/login')) return;

    // Already shown / subscribed?
    const stored = localStorage.getItem(KEY);
    if (stored) {
      // Migrate: if user has a saved code, expose it so they can still see it
      try {
        const parsed = JSON.parse(stored);
        if (parsed && parsed.code) setCode(parsed.code);
      } catch (_e) {
        // ignore
      }
      return;
    }

    // Show after 12s OR exit-intent on desktop
    const t = setTimeout(() => setIsOpen(true), 12000);
    const onLeave = (e) => {
      if (e.clientY <= 0) {
        setIsOpen(true);
        document.removeEventListener('mouseout', onLeave);
        clearTimeout(t);
      }
    };
    document.addEventListener('mouseout', onLeave);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mouseout', onLeave);
    };
  }, []);

  const close = () => {
    setIsOpen(false);
    localStorage.setItem(KEY, JSON.stringify({ closed: true, ts: Date.now() }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), lang }),
      });
      const data = await res.json().catch(() => ({}));
      const codeStr = data.code || '';
      setCode(codeStr);
      setSubmitted(true);
      localStorage.setItem(KEY, JSON.stringify({ email, code: codeStr, ts: Date.now() }));
    } catch (_e) {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const TXT = lang === 'es' ? {
    eyebrow: 'Acceso preferente',
    heading: 'Sé la primera en saber',
    body: 'Recibe noticias de nuevas obras antes que nadie y obtén un 10% de descuento en tu primera compra.',
    placeholder: 'tu@correo.com',
    cta: 'Quiero el descuento',
    no_thanks: 'No, gracias',
    thanks_title: '¡Gracias!',
    thanks_body: 'Aquí está tu código:',
    thanks_use: 'Úsalo al pagar. Te enviamos también una copia a tu correo.',
    privacy: 'Sin spam. Cancela cuando quieras.',
  } : {
    eyebrow: 'Early access',
    heading: 'Be the first to know',
    body: 'Get notified of new pieces before anyone else and receive 10% off your first purchase.',
    placeholder: 'you@email.com',
    cta: 'Send my discount',
    no_thanks: 'No, thanks',
    thanks_title: 'Thank you',
    thanks_body: 'Here is your code:',
    thanks_use: 'Use it at checkout. We have also emailed you a copy.',
    privacy: 'No spam. Unsubscribe anytime.',
  };

  return (
    <div className="np-overlay" onClick={close} data-testid="newsletter-popup">
      <div className="np-card" onClick={(e) => e.stopPropagation()}>
        <button className="np-close" onClick={close} aria-label="close">
          <X size={20} />
        </button>

        {!submitted ? (
          <>
            <span className="np-eyebrow">— {TXT.eyebrow}</span>
            <h2 className="np-heading">{TXT.heading}</h2>
            <p className="np-body">{TXT.body}</p>
            <form className="np-form" onSubmit={submit}>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={TXT.placeholder}
                data-testid="newsletter-email"
              />
              <button type="submit" disabled={submitting} data-testid="newsletter-submit">
                {submitting ? '…' : TXT.cta}
              </button>
            </form>
            <button className="np-skip" onClick={close} data-testid="newsletter-skip">
              {TXT.no_thanks}
            </button>
            <p className="np-privacy">{TXT.privacy}</p>
          </>
        ) : (
          <>
            <div className="np-check"><Check size={28} strokeWidth={1.5} /></div>
            <h2 className="np-heading">{TXT.thanks_title}</h2>
            <p className="np-body">{TXT.thanks_body}</p>
            {code && <p className="np-code" translate="no">{code}</p>}
            <p className="np-privacy">{TXT.thanks_use}</p>
          </>
        )}
      </div>
    </div>
  );
};

export default NewsletterPopup;
